import { DOMAIN_USERNAME_MAP } from '../data/domainUsernameMap.js';

/**
 * Check if the current hostname is a custom domain (not the default twinesite.com).
 *
 * @returns {boolean} true if the current hostname is a custom domain.
 */
export const isCustomDomain = () => {
  const hostname = window.location.hostname;
  return !hostname.includes("twinesite.com") && hostname !== "localhost";
};

/**
 * Return the Twine username corresponding to the current hostname.
 *
 * If the hostname is not in the list of known hostnames, returns the first
 * subdomain as the username.
 *
 * @returns {string} the Twine username associated with the current hostname.
 */
export const getUsernameFromURL = () => {
  const hostname = window.location.hostname;
  
  // Strip 'www.' prefix if present to normalize the domain
  const normalizedDomain = hostname.replace(/^www\./, '');
  
  // Check if the normalized domain exists in our object
  const username = DOMAIN_USERNAME_MAP[normalizedDomain];
  
  if (username) {
    return username;
  }
  
  // Fallback: return the first subdomain from the original hostname
  return hostname.split(".")[0];
};

export function beginsWithVowel(string) {
  return /^[aeiouAEIOU]/.test(string);
}

/**
 * Video embed service configurations
 * Each service defines patterns to match URLs and how to create embeds
 */
const EMBED_SERVICES = {
  youtube: {
    name: "YouTube",
    patterns: [
      // youtu.be format
      /(?:https?:\/\/)?(?:www\.)?youtu\.be\/([a-zA-Z0-9_-]+)/,
      // youtube.com watch format
      /(?:https?:\/\/)?(?:www\.)?youtube\.com\/watch\?v=([a-zA-Z0-9_-]+)/,
    ],
    extractId: (url) => {
      for (const pattern of EMBED_SERVICES.youtube.patterns) {
        const match = url.match(pattern);
        if (match) {
          return match[1];
        }
      }
      return null;
    },
    createEmbed: (videoId, width = 560, height = 315) => {
      return `<iframe width="${width}" height="${height}" src="https://www.youtube.com/embed/${videoId}" title="YouTube video player" frameborder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" referrerpolicy="strict-origin-when-cross-origin" allowfullscreen></iframe>`;
    },
    urlRegex:
      /https?:\/\/(?:www\.)?(?:youtube\.com\/watch\?v=|youtu\.be\/)([a-zA-Z0-9_-]+)(?:[^\s]*)?/g,
  },
  // Future services can be added here:
  // vimeo: { ... },
  // tiktok: { ... },
  // etc.
};

/**
 * Extract video ID from a URL for any supported service
 *
 * @param {string} url - The video URL
 * @returns {Object|null} - Object with service name and video ID, or null if not found
 */
export const extractVideoId = (url) => {
  for (const [serviceName, service] of Object.entries(EMBED_SERVICES)) {
    const videoId = service.extractId(url);
    if (videoId) {
      return { service: serviceName, id: videoId };
    }
  }
  return null;
};

/**
 * Create an embed iframe for a specific service and video ID
 *
 * @param {string} serviceName - The name of the embed service
 * @param {string} videoId - The video ID
 * @param {number} width - The iframe width (default: 560)
 * @param {number} height - The iframe height (default: 315)
 * @returns {string|null} - The iframe HTML string or null if service not found
 */
export const createVideoEmbed = (
  serviceName,
  videoId,
  width = 560,
  height = 315
) => {
  const service = EMBED_SERVICES[serviceName];
  if (service && service.createEmbed) {
    return service.createEmbed(videoId, width, height);
  }
  return null;
};

/**
 * Convert video links in text to iframe embeds for all supported services
 * Processes text and replaces video URLs with iframe embeds
 *
 * @param {string} text - The text containing video links
 * @param {number} width - The iframe width (default: 560)
 * @param {number} height - The iframe height (default: 315)
 * @returns {string} - The text with video links converted to embeds
 */
export const convertVideoLinksToEmbeds = (text, width = 560, height = 315) => {
  if (!text || typeof text !== "string") {
    return text;
  }

  let processedText = text;

  // Process each service
  for (const [serviceName, service] of Object.entries(EMBED_SERVICES)) {
    if (service.urlRegex) {
      processedText = processedText.replace(service.urlRegex, (match) => {
        const videoData = extractVideoId(match);
        if (videoData && videoData.service === serviceName) {
          const embed = createVideoEmbed(
            serviceName,
            videoData.id,
            width,
            height
          );
          if (embed) {
            // Add double line breaks before and after embed for proper Markdown paragraph separation
            return `\n\n${embed}\n\n`;
          }
        }
        return match; // Return original if we can't create embed
      });
    }
  }

  return processedText;
};
